import { apiFetch } from "./api";
import { escapeHtml } from "./utils";
import type { DirectoryUser } from "./types";

// Framework-agnostic (no dom.ts dependency) - safe for React to import
// directly. The old inline @mention autocomplete dropdown that used to
// live in this file was wired against the vanilla comment form's specific
// element ids/classes; now that the comment form is a React component
// (MentionInput.tsx), that dropdown UI is reimplemented there instead,
// reusing loadDirectory()/highlightMentions() from here.
let directory: DirectoryUser[] = [];
let loaded = false;

export async function loadDirectory(): Promise<void> {
  try {
    const res = await apiFetch("/api/users/directory");
    if (!res.ok) return;
    const data = await res.json();
    directory = Array.isArray(data.users) ? data.users : [];
    loaded = true;
  } catch {
    // Best effort - mentions just won't autocomplete/highlight until the
    // next call (e.g. the next protected-features refresh tick) succeeds.
  }
}

export function getDirectory(): DirectoryUser[] {
  return directory;
}

// Shared with MentionInput.tsx (the React autocomplete dropdown) so the
// "what's being typed after the last @" parsing logic exists in one place.
export function findMentionFragment(value: string, caret: number): { start: number; query: string } | null {
  const upToCaret = value.slice(0, caret);
  const atIndex = upToCaret.lastIndexOf("@");
  if (atIndex === -1) return null;
  const fragment = upToCaret.slice(atIndex + 1);
  if (/\s/.test(fragment)) return null;
  return { start: atIndex, query: fragment };
}

// text is assumed to already be HTML-escaped (from escapeHtml) - this only
// wraps @FullName occurrences that match a known user, it never introduces
// unescaped user input into the DOM.
export function highlightMentions(escapedText: string): string {
  if (!loaded || !directory.length) return escapedText;
  let result = escapedText;
  for (const u of directory) {
    const name = u.fullName.trim();
    if (!name) continue;
    const escapedName = escapeHtml(name).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`@${escapedName}\\b`, "gi");
    result = result.replace(re, (match) => `<span class="comment-mention">${match}</span>`);
  }
  return result;
}
